import { type CSSProperties, useId } from 'react';

export interface DatePickerProps {
  /** ISO `YYYY-MM-DD`. */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  hint?: string;
  /** The server's translated issue for this field: shown in place of the hint, and the field is marked invalid. */
  error?: string;
  /** ISO bounds, passed to the native input. */
  min?: string;
  max?: string;
  disabled?: boolean;
  width?: CSSProperties['inlineSize'];
  style?: CSSProperties;
}

/**
 * Single-date field. The value is always displayed as ISO `2026-09-17`
 * (filters use ISO, D34) whatever the browser locale: the native date input
 * sits on top, transparent, so the calendar picker, keyboard entry and form
 * semantics stay the browser's own.
 */
export function DatePicker({ value, onChange, label, hint, error, min, max, disabled, width, style }: DatePickerProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const note = error ?? hint;
  const classes = ['fs-control', 'fs-control--date'];
  if (disabled) classes.push('fs-control--disabled');
  const control = (
    <span className={classes.join(' ')} style={{ inlineSize: width, ...style }}>
      <span
        className={value ? 'fs-control__date-value fs-num' : 'fs-control__date-value fs-num fs-control__date-value--empty'}
        dir="ltr"
        aria-hidden="true"
      >
        {value || 'YYYY-MM-DD'}
      </span>
      <span className="fs-control__calendar" aria-hidden="true" />
      <input
        id={label ? id : undefined}
        type="date"
        dir="ltr"
        className="fs-control__date-native"
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={note ? hintId : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
    </span>
  );
  if (!label && !note) return control;
  return (
    <div className={error ? 'fs-field fs-field--invalid' : 'fs-field'}>
      {label && (
        <label className="fs-field__label" htmlFor={id}>
          {label}
        </label>
      )}
      {control}
      {note && (
        <span id={hintId} className="fs-field__hint">
          {note}
        </span>
      )}
    </div>
  );
}
